<script setup>
import { Bell, BookOpen, Clock3, SquareStack } from 'lucide-vue-next'
import { timeAgo } from '@/utils/datetimeFormat'

defineProps({
  notification: { type: Object, required: true },
  compact: { type: Boolean, default: false },
})

defineEmits(['select'])

function iconFor(type) {
  if (type === 'Review Due') return SquareStack
  if (type === 'Continue Learning') return BookOpen
  if (type === 'Daily Reminder') return Clock3
  return Bell
}
</script>

<template>
  <button
    type="button"
    class="notification-row"
    :class="{ 'notification-row--unread': !notification.is_read, 'notification-row--compact': compact }"
    @click="$emit('select', notification)"
  >
    <span
      class="notification-icon"
      aria-hidden="true"
    >
      <component
        :is="iconFor(notification.notification_type)"
        :size="compact ? 16 : 18"
        :stroke-width="1.8"
      />
    </span>
    <span class="notification-copy">
      <strong>{{ notification.title }}</strong>
      <span
        v-if="notification.message"
        class="notification-message"
      >{{ notification.message }}</span>
      <time :datetime="notification.creation">{{ timeAgo(notification.creation) }}</time>
    </span>
    <span
      v-if="!notification.is_read"
      class="notification-unread-dot"
      :aria-label="$t('notifications.unread')"
    />
  </button>
</template>

<style scoped>
.notification-row {
  width: 100%;
  padding: 13px 15px;
  display: flex;
  align-items: flex-start;
  gap: 11px;
  color: #2d2c3b;
  /* Keep the row opaque while it is being tapped/focused on mobile. */
  background: var(--surface-card);
  border: 0;
  border-bottom: 1px solid #f0eff5;
  text-align: left;
  cursor: pointer;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
}

.notification-row:hover,
.notification-row--unread {
  background: #f7f7ff;
}

.notification-row:active,
.notification-row:focus {
  background: #f7f7ff;
}

.notification-row:focus-visible {
  outline: 2px solid #8179e8;
  outline-offset: -2px;
}

.notification-row--compact {
  padding: 11px 13px;
}

.notification-icon {
  width: 29px;
  height: 29px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  color: #5148d2;
  background: #eeefff;
  border-radius: 8px;
}

.notification-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.notification-copy strong {
  overflow-wrap: anywhere;
  color: #2d2c3b;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.notification-message {
  overflow-wrap: anywhere;
  color: #686777;
  font-size: 12px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.notification-copy time {
  color: #8a8997;
  font-size: 11px;
}

.notification-unread-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  margin-top: 7px;
  border-radius: 50%;
  background: #554bd7;
}
</style>
